import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Sword, Activity, Users, Shield, ArrowLeft, Radio, ChessBishop } from 'lucide-react'

const STATUS_LABELS = {
  waiting: { label: 'SYNCHING', color: 'text-orange-500 bg-orange-500/10 border-orange-500/20' },
  active: { label: 'LIVE SIGNAL', color: 'text-green-500 bg-green-500/10 border-green-500/20' },
  finished: { label: 'ARCHIVED', color: 'text-zinc-500 bg-zinc-900 border-zinc-800' },
  closed: { label: 'TERMINATED', color: 'text-red-500 bg-red-500/10 border-red-500/20' },
}

export default function RoomHeader({ room, role, guestJoined, gameActive }) {
  const navigate = useNavigate()
  const statusConfig = STATUS_LABELS[room?.status] || STATUS_LABELS.waiting

  return (
    <header className="relative z-50 bg-black/80 backdrop-blur-md border-b border-white/5 px-4 md:px-8 py-3 flex items-center gap-6">
      {/* 1. BRAND / BACK ACTION */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="p-2 hover:bg-white/5 rounded-lg transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 text-zinc-500 group-hover:text-orange-500 transition-colors" />
        </button>
        
        <div className="flex items-center gap-2">
          <div className="bg-orange-600 p-1.5 rounded rotate-3 group-hover:rotate-0 transition-transform">
             <ChessBishop size={16} className="text-white" />
          </div>
          <span className="font-black text-xl tracking-tighter uppercase italic hidden sm:block">
            Duel<span className="text-orange-600">o</span>
          </span>
        </div>
      </div>

      <div className="h-6 w-[1px] bg-white/10 hidden md:block" />

      {/* 2. MATCH INFO HUD */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex flex-col">
          <span className="text-[10px] font-mono text-zinc-500 uppercase leading-none mb-1">Session_ID</span>
          <span className="text-sm font-mono font-bold tracking-tight text-orange-500 truncate">
            {room?.id || 'LOCAL_GHOST'}
          </span>
        </div>
      </div>

      <div className="flex-1" />

      {/* 3. PLAYER PRESENCE (THE HUD PILLS) */}
      <div className="hidden lg:flex items-center gap-3">
        <PlayerPill role="Host" active={true} isSelf={role === 'creator'} />
        <div className="w-4 h-[1px] bg-white/10" />
        <PlayerPill role="Guest" active={guestJoined} isSelf={role === 'guest'} />
      </div>

      {/* 4. LIVE STATUS BADGE */}
      <div className="flex items-center gap-3">
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md border font-mono text-[10px] font-black uppercase tracking-widest ${statusConfig.color}`}
        >
          {room?.status === 'active' && (
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
          )}
          {statusConfig.label}
        </motion.div>
      </div>
    </header>
  )
}

function PlayerPill({ role, active, isSelf }) {
  return (
    <div className={`flex items-center gap-3 transition-all ${active ? 'opacity-100' : 'opacity-30'}`}>
      <div className="relative">
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${
          active ? 'bg-orange-600/10 border-orange-500/40 text-orange-500' : 'bg-zinc-900 border-white/5 text-zinc-600'
        }`}>
          <Users size={14} />
        </div>
        {isSelf && (
          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-black" />
        )}
      </div>
      <div className="flex flex-col">
        <span className="text-[9px] font-mono text-zinc-500 uppercase leading-none">{role}</span>
        <span className={`text-xs font-bold uppercase tracking-tighter ${active ? 'text-zinc-100' : 'text-zinc-600'}`}>
          {active ? 'CONNECTED' : 'OFFLINE'}
        </span>
      </div>
    </div>
  )
}